/**
 * Application navigation — original module tree for Universal POS + Accounting.
 * Permission keys gate visibility; routes map to React pages.
 */
export const APP_NAV = [
    {
        key: 'dashboard',
        label: 'Dashboard',
        path: '/',
        permission: 'dashboard',
        icon: 'LayoutDashboard',
    },
    {
        key: 'bank',
        label: 'Bank',
        permission: 'bank',
        icon: 'Landmark',
        children: [
            { key: 'bank-accounts', label: 'Account Balances', path: '/bank/accounts', permission: 'bank' },
            { key: 'bank-payments', label: 'Bank Payments', path: '/bank/payments', permission: 'bank' },
            { key: 'bank-receipts', label: 'Bank Receipts', path: '/bank/receipts', permission: 'bank' },
            { key: 'bank-transfers', label: 'Transfers', path: '/bank/transfers', permission: 'bank' },
        ],
    },
    {
        key: 'sales',
        label: 'Sales',
        permission: 'sales',
        icon: 'CircleDollarSign',
        children: [
            { key: 'sales-invoices', label: 'Invoices', path: '/sales/invoices', permission: 'sales.invoices' },
            { key: 'sales-quotations', label: 'Quotations', path: '/sales/quotations', permission: 'sales.invoices' },
            { key: 'sales-returns', label: 'Returns', path: '/sales/returns', permission: 'sales.invoices' },
            { key: 'sales-receipts', label: 'Receipts', path: '/sales/receipts', permission: 'sales.receipts' },
            { key: 'sales-history', label: 'Sales History', path: '/sales', permission: 'sales' },
            { key: 'customers', label: 'Customers', path: '/customers', permission: 'customers' },
        ],
    },
    {
        key: 'purchases',
        label: 'Purchases',
        permission: 'purchases',
        icon: 'Wallet',
        children: [
            { key: 'purchase-bills', label: 'Bills & POs', path: '/purchases/bills', permission: 'purchases' },
            { key: 'purchase-returns', label: 'Purchase Returns', path: '/purchases/returns', permission: 'purchases' },
            { key: 'purchase-payments', label: 'Payments', path: '/purchases/payments', permission: 'purchases' },
            { key: 'suppliers', label: 'Suppliers', path: '/purchases/suppliers', permission: 'purchases' },
        ],
    },
    {
        key: 'inventory',
        label: 'Inventory',
        permission: 'products',
        icon: 'Package',
        children: [
            { key: 'products', label: 'Products', path: '/products', permission: 'products' },
            { key: 'inventory-locations', label: 'Locations', path: '/inventory/locations', permission: 'inventory' },
            { key: 'inventory-adjustments', label: 'Adjustments', path: '/inventory/adjustments', permission: 'inventory.adjust' },
            { key: 'inventory-transfers', label: 'Transfers', path: '/inventory/transfers', permission: 'inventory.adjust' },
            { key: 'inventory-batches', label: 'Batches', path: '/inventory/batches', permission: 'products.batches' },
            { key: 'inventory-low-stock', label: 'Low Stock', path: '/inventory/low-stock', permission: 'inventory' },
            { key: 'inventory-units', label: 'Units & Kits', path: '/inventory/units', permission: 'inventory' },
        ],
    },
    {
        key: 'accounting',
        label: 'Accounting',
        permission: 'accounting',
        icon: 'BookOpen',
        children: [
            { key: 'chart', label: 'Chart of Accounts', path: '/accounting/chart', permission: 'accounting.chart' },
            { key: 'journals', label: 'Journals', path: '/accounting/journals', permission: 'accounting.journals' },
            { key: 'expense-income', label: 'Expense & Income', path: '/accounting/expense-income', permission: 'accounting.journals' },
            { key: 'financial-reports', label: 'Financial Reports', path: '/accounting/reports', permission: 'accounting.reports' },
            { key: 'fiscal', label: 'Fiscal Year', path: '/accounting/fiscal', permission: 'accounting.close' },
        ],
    },
    {
        key: 'tax',
        label: 'Tax & FBR',
        permission: 'fbr',
        icon: 'Receipt',
        children: [
            { key: 'tax-rates', label: 'Tax Rates', path: '/tax/rates', permission: 'fbr' },
            { key: 'fbr', label: 'FBR Dashboard', path: '/tax/fbr', permission: 'fbr' },
        ],
    },
    {
        key: 'printing',
        label: 'Printing',
        permission: 'sales.print',
        icon: 'Printer',
        children: [
            { key: 'print-templates', label: 'Templates', path: '/printing/templates', permission: 'sales.print' },
        ],
    },
    {
        key: 'reports',
        label: 'Reports',
        path: '/reports',
        permission: 'reports',
        icon: 'BarChart3',
    },
    {
        key: 'enterprise',
        label: 'Enterprise',
        permission: 'settings',
        icon: 'Building2',
        children: [
            { key: 'audit-logs', label: 'Audit & Login', path: '/enterprise/audit', permission: 'audit' },
            { key: 'company-access', label: 'Company Access', path: '/enterprise/companies', permission: 'users' },
            { key: 'access-scopes', label: 'Access Restrictions', path: '/enterprise/scopes', permission: 'users.update' },
            { key: 'scheduled-tasks', label: 'Scheduled Tasks', path: '/enterprise/tasks', permission: 'settings.update' },
        ],
    },
    {
        key: 'settings',
        label: 'Settings',
        path: '/settings',
        permission: 'settings',
        icon: 'Settings',
    },
];

export function filterNavByPermissions(nav, permissions = [], isSuperAdmin = false) {
    const hasAll = permissions.includes('all');
    const can = (perm) => !perm || hasAll || permissions.includes(perm);

    return nav
        .map((item) => {
            if (item.children) {
                const children = item.children.filter((c) => can(c.permission));
                if (!children.length && !can(item.permission)) return null;
                if (!children.length) return null;
                return { ...item, children };
            }
            return can(item.permission) ? item : null;
        })
        .filter(Boolean);
}

export function findNavItemByPath(pathname) {
    let base = pathname
        .replace(/\/kits\/new$/, '')
        .replace(/\/(new|grant|categories|opening)$/, '')
        .replace(/\/\d+\/(edit|payment|opening)$/, '')
        .replace(/\/\d+$/, '')
        .replace(/\/$/, '') || '/';
    for (const item of APP_NAV) {
        if (item.path === base || item.path === pathname) return item;
        if (item.children) {
            const child = item.children.find((c) => c.path === base || c.path === pathname || pathname.startsWith(c.path + '/'));
            if (child) return { ...child, parent: item };
        }
    }
    if (pathname.startsWith('/users')) return { label: 'Users', path: '/users' };
    if (pathname.startsWith('/super-admin')) return { label: 'Companies', path: '/super-admin' };
    if (pathname === '/profile') return { label: 'Profile', path: '/profile' };
    return { label: 'Dashboard', path: '/' };
}

export function buildBreadcrumbs(pathname) {
    const crumbs = [{ label: 'Home', path: '/' }];
    const match = findNavItemByPath(pathname);
    if (match?.parent) {
        crumbs.push({ label: match.parent.label });
        crumbs.push({ label: match.label, path: match.path });
    } else if (match && match.path !== '/') {
        crumbs.push({ label: match.label, path: match.path });
    }
    if (pathname.endsWith('/kits/new')) {
        crumbs.push({ label: 'New Kit', path: pathname });
    } else if (pathname.includes('/opening')) {
        crumbs.push({ label: 'Opening Balance', path: pathname });
    } else if (pathname.endsWith('/new')) {
        crumbs.push({ label: 'New', path: pathname });
    } else if (pathname.endsWith('/grant')) {
        crumbs.push({ label: 'Grant Access', path: pathname });
    } else if (pathname.endsWith('/payment')) {
        crumbs.push({ label: 'Payment', path: pathname });
    } else if (pathname.endsWith('/categories')) {
        crumbs.push({ label: 'Categories', path: pathname });
    } else if (pathname.endsWith('/edit')) {
        crumbs.push({ label: 'Edit', path: pathname });
    } else if (/\/\d+$/.test(pathname) && !pathname.endsWith('/new')) {
        crumbs.push({ label: 'Detail', path: pathname });
    }
    return crumbs;
}
